import type {
  OffSiteLocationID,
  PotwID,
  QuestionnaireOffSiteLocation,
  QuestionnairePotw,
} from '../../types/questionnaires.ts';
import type { CalculationLine, TransferComputed } from '../releases-calculate.ts';
import type { FormRLeafLine } from './form-r-sections.ts';
import type { CasOrCategory, ReleaseProfileID } from '../../types/db.ts';
import type {
  TRIQuantityBasisEstimationCodeType,
  TRISourceReductionQuantityDataType,
  TRITotalYearlyQuantityDataType,
  TRIToxicEquivalencyIdentificationType,
  TRITransferLocationDataType,
} from '../../types/form-r.ts';
import { formRDecimalToString, formRTotalQuantityToString } from './form-r-xml-builder.ts';
import type { Section8Quantities, Section8Total } from './form-r-section-8-sums.ts';
import { counties } from '../../data/tri-counties.ts';
import type { ThresholdBrief, Warning } from '../../types/tri.ts';
import { Gram, UnitsTracker } from '../formulas/units.ts';
import { dioxinCongenerToFormRField, dioxinsCategory, dioxinToxicEquivalencyFields } from '../../data/tri-dioxins.ts';
import { triStates } from '../../data/tri-states.ts';

export interface ReleaseByChemicalAndFormRLine {
  formRLine: FormRLeafLine;
  casOrCategory: CasOrCategory;
  amountReleased: number;
  amountUnits: 'pounds' | 'grams';
  transfers: TransferComputed[];
}

export type ReleasesByChemicalAndFormRLine = Map<`${CasOrCategory}-${FormRLeafLine}`, ReleaseByChemicalAndFormRLine>;

export function formRBasisFromTransfers(
  transfersComputed: TransferComputed[],
  context: { warnings: Warning[]; formRLine: FormRLeafLine; chemicalName: string; casOrCategory: CasOrCategory },
): TRIQuantityBasisEstimationCodeType {
  const amountByBasis = new Map<TRIQuantityBasisEstimationCodeType, number>();
  let commonUnits: null | UnitsTracker = null;

  for (const transfer of transfersComputed) {
    for (const line of transfer.calculationLines) {
      if (line.basis === '') {
        if (line.releaseProfileID) {
          context.warnings.push({
            warningID: 'TW1073',
            type: 'releaseProfile',
            releaseProfileID: line.releaseProfileID,
            releaseProfileName: line.releaseProfileName,
            message: `Process "${line.releaseProfileProcessName}" using profile "${line.releaseProfileName}" - No basis for estimate was found.`,
          });
        } else {
          context.warnings.push({
            warningID: 'TW1074',
            type: 'internalConsistencyCheck',
            message: `Process "${line.releaseProfileProcessName}" using profile "${line.releaseProfileName}" - No basis for estimate was found.`,
          });
        }
      }

      const basis = line.basis === '' ? 'O' : line.basis;

      const lineUnits = UnitsTracker.fromParsedUnitString(line.computationUnits);
      if (!commonUnits) {
        commonUnits = lineUnits;
      }

      const converted = lineUnits.convertTo(commonUnits, line.computationResult);
      if (typeof converted === 'number') {
        amountByBasis.set(basis, (amountByBasis.get(basis) ?? 0) + converted);
      } else {
        // Calculation warning TW1076 should prevent us from getting here in practice:
        context.warnings.push({
          warningID: 'TW1119',
          type: 'internalConsistencyCheck',
          message: `Process "${line.releaseProfileProcessName}" using profile "${line.releaseProfileName}" - Unable to convert units ${line.computationUnits} to ${commonUnits.toString()}.`,
        });
      }
    }
  }

  const amountByBasisSorted = Array.from(amountByBasis.entries()).sort((a, b) => b[1] - a[1]);
  const basisWithMost = amountByBasisSorted[0]?.[0];

  if (basisWithMost) {
    return basisWithMost;
  }

  let releaseProfile: { releaseProfileID: ReleaseProfileID; releaseProfileName: string } | null = null;
  releaseProfileLoop: for (const transfer of transfersComputed) {
    for (const line of transfer.calculationLines) {
      if (line.releaseProfileID) {
        releaseProfile = {
          releaseProfileID: line.releaseProfileID,
          releaseProfileName: line.releaseProfileName,
        };
        break releaseProfileLoop;
      }
    }
  }

  const message = `While processing Form R line ${context.formRLine} for ${context.chemicalName} (${context.casOrCategory}) we were unable to find the basis code for the release, using "O" (Other Approaches).`;

  if (releaseProfile) {
    context.warnings.push({
      warningID: 'TW1029',
      type: 'releaseProfile',
      releaseProfileID: releaseProfile.releaseProfileID,
      releaseProfileName: releaseProfile.releaseProfileName,
      message,
    });
  } else {
    context.warnings.push({
      warningID: 'TW1064',
      // There is a chance we may have a parent question or release profile we could point the user to, but we don't
      // have the context here, and in theory I would expect that to be caught sooner:
      type: 'internalConsistencyCheck',
      message,
    });
  }

  return 'O';
}

export type FormRTransferLocationRegistrar = (
  location: QuestionnairePotw | QuestionnaireOffSiteLocation | TRITransferLocationDataType,
) => TRITransferLocationDataType;
export function formRRegisterTransferLocation(
  list: TRITransferLocationDataType[],
  mapping: Map<PotwID | OffSiteLocationID, TRITransferLocationDataType>,
  location: QuestionnairePotw | QuestionnaireOffSiteLocation | TRITransferLocationDataType,
  warnings: Warning[],
  transferLocationsWithMissingData: Set<OffSiteLocationID | PotwID>,
): TRITransferLocationDataType {
  if ('TransferLocationSequenceNumber' in location) {
    const updated = {
      ...location,
      TransferLocationSequenceNumber: list.length + 1,
    };
    list.push(updated);
    return updated;
  }

  const id = 'potwID' in location ? location.potwID : location.offSiteLocationID;
  const type = 'potwID' in location ? 'POTW' : 'off-site location';
  const existing = mapping.get(id);
  if (existing) {
    return existing;
  }

  let StateName = location.state.toUpperCase();
  let isUS = true;
  if (!location.state && 'province' in location && location.province) {
    StateName = location.province.toUpperCase();
    isUS = false;
  } else if (StateName && !triStates.includes(StateName)) {
    warnings.push({
      warningID: 'TW1128',
      type: 'questionnaire',
      subType: id,
      casOrCategory: null,
      message: `A ${type} does not appear to have a valid state listed: "${StateName}"`,
      blocksPriorYearSection8Calculation: false,
    });
    transferLocationsWithMissingData.add(id);
  }

  if (location.name.trim().length === 0) {
    warnings.push({
      warningID: 'TW1030',
      type: 'questionnaire',
      subType: id,
      casOrCategory: null,
      message: `A ${type} was not given a name. ID: ${id}`,
      blocksPriorYearSection8Calculation: false,
    });
    transferLocationsWithMissingData.add(id);
  }

  if (location.address.trim().length === 0) {
    warnings.push({
      warningID: 'TW1031',
      type: 'questionnaire',
      subType: id,
      casOrCategory: null,
      message: `The ${type} "${location.name}" is missing an address.`,
      blocksPriorYearSection8Calculation: false,
    });
    transferLocationsWithMissingData.add(id);
  }

  if (location.city.trim().length === 0) {
    warnings.push({
      warningID: 'TW1032',
      type: 'questionnaire',
      subType: id,
      casOrCategory: null,
      message: `The ${type} "${location.name}" is missing a city.`,
      blocksPriorYearSection8Calculation: false,
    });
    transferLocationsWithMissingData.add(id);
  }

  if (location.county.trim().length === 0) {
    warnings.push({
      warningID: 'TW1033',
      type: 'questionnaire',
      subType: id,
      casOrCategory: null,
      message: `The ${type} "${location.name}" is missing a county.`,
      blocksPriorYearSection8Calculation: false,
    });
    transferLocationsWithMissingData.add(id);
  }

  if (StateName.trim().length === 0) {
    warnings.push({
      warningID: 'TW1034',
      type: 'questionnaire',
      subType: id,
      casOrCategory: null,
      message: `The ${type} "${location.name}" is missing a state or province.`,
      blocksPriorYearSection8Calculation: false,
    });
    transferLocationsWithMissingData.add(id);
  }

  if (location.zip.trim().length === 0) {
    warnings.push({
      warningID: 'TW1035',
      type: 'questionnaire',
      subType: id,
      casOrCategory: null,
      message: `The ${type} "${location.name}" is missing a ZIP code.`,
      blocksPriorYearSection8Calculation: false,
    });
    transferLocationsWithMissingData.add(id);
  }

  if (
    isUS &&
    location.county &&
    StateName &&
    Array.isArray(counties[StateName]) &&
    !counties[StateName]!.includes(location.county.trim().toUpperCase()) &&
    !transferLocationsWithMissingData.has(id)
  ) {
    warnings.push({
      warningID: 'TW1036',
      type: 'questionnaire',
      subType: id,
      casOrCategory: null,
      message: `The ${type} "${location.name}" lists an unknown county "${location.county}". Expected one of: ${counties[StateName]!.join(', ')}`,
      blocksPriorYearSection8Calculation: false,
    });
    transferLocationsWithMissingData.add(id);
  }

  const transformed: TRITransferLocationDataType = {
    TransferLocationSequenceNumber: list.length + 1,
    FacilitySiteName: location.name,
    POTWIndicator: 'potwID' in location,
    LocationAddress: {
      LocationAddressText: location.address,
      LocalityName: location.city,
      CountyIdentity: {
        CountyCode: null, // It's unclear what this would be (do counties even have standard codes/abbreviations?)
        CountyCodeListIdentifier: null, // I'm unable to find any examples of what this would be.
        CountyName: location.county,
      },
      AddressPostalCode: {
        value: location.zip,
        attrs: {}, // There is no description of the meaning of the AddressPostalCodeContext attribute, so leaving it out.
      },
      StateIdentity: {
        StateCode: null, // It's not clear if this is an abbreviation like "AZ" or something else, leaving as null for now.
        StateCodeListIdentifier: null, // I'm unable to find any examples of what this would be.
        StateName,
      },
      CountryIdentity:
        'country' in location && location.country
          ? {
              CountryCode: null, // It's not clear if this is an abbreviation like "NZ" or something else, leaving as null for now.
              CountryCodeListIdentifier: null, // I'm unable to find any examples of what this would be.
              CountryName: location.country,
            }
          : null,

      // While the schema implies these fields are available, I'm not convinced it's true. In any case,
      // they're optional and the address info above should be sufficient in any case:
      LocationDescriptionText: null,
      SupplementalLocationText: null,
      TribalIdentity: null,
      TribalLandIndicator: null,
      TribalLandName: null,
    },

    // Only applicable to Section 6.2:
    TransferQuantity: [],
    ControlledLocationIndicator:
      'offSiteLocationID' in location ? location.isControlledByFacilityOrParentCompany : null,
    RCRAIdentificationNumber: 'rcraNum' in location ? location.rcraNum : null,
    EPARegistryIdentification: 'epaID' in location ? location.epaID : null,
  };
  list.push(transformed);
  mapping.set(id, transformed);
  return transformed;
}

/**
 * @see formRDecimalToString
 */
function formRRoundNumber(number: number): number {
  return Number(formRDecimalToString(number));
}

export function formRNumbersToSection8Value(totals: Section8Total[]): Section8Total {
  let TotalQuantity: number | null = null;
  let ToxicEquivalencyIdentification: TRIToxicEquivalencyIdentificationType | null = null;

  for (const item of totals) {
    if (item === 'NA') {
      continue;
    }

    TotalQuantity = (TotalQuantity ?? 0) + formRRoundNumber(item.TotalQuantity);

    if (!item.ToxicEquivalencyIdentification) {
      continue;
    }

    if (!ToxicEquivalencyIdentification) {
      ToxicEquivalencyIdentification = {
        ToxicEquivalency1Value: 0,
        ToxicEquivalency2Value: 0,
        ToxicEquivalency3Value: 0,
        ToxicEquivalency4Value: 0,
        ToxicEquivalency5Value: 0,
        ToxicEquivalency6Value: 0,
        ToxicEquivalency7Value: 0,
        ToxicEquivalency8Value: 0,
        ToxicEquivalency9Value: 0,
        ToxicEquivalency10Value: 0,
        ToxicEquivalency11Value: 0,
        ToxicEquivalency12Value: 0,
        ToxicEquivalency13Value: 0,
        ToxicEquivalency14Value: 0,
        ToxicEquivalency15Value: 0,
        ToxicEquivalency16Value: 0,
        ToxicEquivalency17Value: 0,
        ToxicEquivalencyNAIndicator: false,
      };
    }

    for (const field of dioxinToxicEquivalencyFields) {
      const value = item.ToxicEquivalencyIdentification[field];
      if (value !== null) {
        ToxicEquivalencyIdentification[field] = ToxicEquivalencyIdentification[field]! + formRRoundNumber(value);
      }
    }
  }

  if (TotalQuantity === null) {
    return 'NA';
  }

  return { TotalQuantity, ToxicEquivalencyIdentification };
}

export function formRExpandSection8Values(
  currentYear: Section8Total,
  past: Section8Total,
): TRITotalYearlyQuantityDataType[] {
  const result: TRITotalYearlyQuantityDataType[] = [];

  if (currentYear === 'NA') {
    result.push({
      YearOffsetMeasure: 0,
      Quantity: {
        choice: 'NA',
        value: true,
      },
      ToxicEquivalencyIdentification: null,
    });
    result.push({
      YearOffsetMeasure: 1,
      Quantity: {
        choice: 'NA',
        value: true,
      },
      ToxicEquivalencyIdentification: null,
    });
    result.push({
      YearOffsetMeasure: 2,
      Quantity: {
        choice: 'NA',
        value: true,
      },
      ToxicEquivalencyIdentification: null,
    });
  } else {
    result.push({
      YearOffsetMeasure: 0,
      Quantity: {
        choice: 0,
        value: {
          TotalQuantity: currentYear.TotalQuantity,
          CalculatorRoundingHintNumber: null, // Used by TRI-MEweb itself, not us.
        },
      },
      ToxicEquivalencyIdentification: currentYear.ToxicEquivalencyIdentification,
    });

    const upcomingVal = currentYear; // We assume 100% ratio for future years.
    result.push({
      YearOffsetMeasure: 1,
      Quantity: {
        choice: 0,
        value: {
          TotalQuantity: upcomingVal.TotalQuantity,
          CalculatorRoundingHintNumber: null, // Used by TRI-MEweb itself, not us.
        },
      },
      ToxicEquivalencyIdentification: null, // Dioxin congener breakdowns are only entered for the current year of Schedule 1 Section 8.
    });
    result.push({
      YearOffsetMeasure: 2,
      Quantity: {
        choice: 0,
        value: {
          TotalQuantity: upcomingVal.TotalQuantity,
          CalculatorRoundingHintNumber: null, // Used by TRI-MEweb itself, not us.
        },
      },
      ToxicEquivalencyIdentification: null, // Dioxin congener breakdowns are only entered for the current year of Schedule 1 Section 8.
    });
  }

  if (past === 'NA') {
    result.push({
      YearOffsetMeasure: -1,
      Quantity: {
        choice: 'NA',
        value: true,
      },
      ToxicEquivalencyIdentification: null,
    });
  } else {
    result.push({
      YearOffsetMeasure: -1,
      Quantity: {
        choice: 0,
        value: {
          TotalQuantity: past.TotalQuantity,
          CalculatorRoundingHintNumber: null, // Used by TRI-MEweb itself, not us.
        },
      },
      ToxicEquivalencyIdentification: null, // Dioxin congener breakdowns are only entered for the current year of Schedule 1 Section 8.
    });
  }

  return result;
}

interface FormREzRelevant {
  energy_offsite_curr_yr_na: string | null;
  energy_offsite_curr_yr_qty: number | null;
  energy_onsite_curr_yr_na: string | null;
  energy_onsite_curr_yr_qty: number | null;
  recyc_offsite_curr_yr_na: string | null;
  recyc_offsite_curr_yr_qty: number | null;
  recyc_onsite_curr_yr_na: string | null;
  recyc_onsite_curr_yr_qty: number | null;
  rel_81a_curr_yr_na: string | null;
  rel_81a_curr_yr_qty: number | null;
  rel_81b_curr_yr_na: string | null;
  rel_81b_curr_yr_qty: number | null;
  rel_81c_curr_yr_na: string | null;
  rel_81c_curr_yr_qty: number | null;
  rel_81d_curr_yr_na: string | null;
  rel_81d_curr_yr_qty: number | null;
  treated_offsite_curr_yr_na: string | null;
  treated_offsite_curr_yr_qty: number | null;
  treated_onsite_curr_yr_na: string | null;
  treated_onsite_curr_yr_qty: number | null;
}

// ToxicEquivalencyIdentification is always returned as null from this function because it is used for computing
// past year values, and dioxin congener breakdowns are only entered for the current year of Schedule 1 Section 8.
export function ezToSection8Value({
  ez,
  section8PriorYearQuantitiesFromCalculation,
  section8CurrentYearQuantities,
  ProductionRatio,
  section,
}: {
  ez: FormREzRelevant | null;
  section8PriorYearQuantitiesFromCalculation: Section8Quantities | null;
  section8CurrentYearQuantities: Section8Quantities;
  ProductionRatio: TRISourceReductionQuantityDataType['ProductionRatio'];
  section: keyof Section8Quantities;
}): Section8Total {
  if (ez) {
    if (section === 'OnsiteUICDisposalQuantity') {
      return ez.rel_81a_curr_yr_na === '1' || ez.rel_81a_curr_yr_qty === null
        ? 'NA'
        : { TotalQuantity: ez.rel_81a_curr_yr_qty, ToxicEquivalencyIdentification: null };
    } else if (section === 'OnsiteOtherDisposalQuantity') {
      return ez.rel_81b_curr_yr_na === '1' || ez.rel_81b_curr_yr_qty === null
        ? 'NA'
        : { TotalQuantity: ez.rel_81b_curr_yr_qty, ToxicEquivalencyIdentification: null };
    } else if (section === 'OffsiteUICDisposalQuantity') {
      return ez.rel_81c_curr_yr_na === '1' || ez.rel_81c_curr_yr_qty === null
        ? 'NA'
        : { TotalQuantity: ez.rel_81c_curr_yr_qty, ToxicEquivalencyIdentification: null };
    } else if (section === 'OffsiteOtherDisposalQuantity') {
      return ez.rel_81d_curr_yr_na === '1' || ez.rel_81d_curr_yr_qty === null
        ? 'NA'
        : { TotalQuantity: ez.rel_81d_curr_yr_qty, ToxicEquivalencyIdentification: null };
    } else if (section === 'OnsiteEnergyRecoveryQuantity') {
      return ez.energy_onsite_curr_yr_na === '1' || ez.energy_onsite_curr_yr_qty === null
        ? 'NA'
        : { TotalQuantity: ez.energy_onsite_curr_yr_qty, ToxicEquivalencyIdentification: null };
    } else if (section === 'OffsiteEnergyRecoveryQuantity') {
      return ez.energy_offsite_curr_yr_na === '1' || ez.energy_offsite_curr_yr_qty === null
        ? 'NA'
        : { TotalQuantity: ez.energy_offsite_curr_yr_qty, ToxicEquivalencyIdentification: null };
    } else if (section === 'OnsiteRecycledQuantity') {
      return ez.recyc_onsite_curr_yr_na === '1' || ez.recyc_onsite_curr_yr_qty === null
        ? 'NA'
        : { TotalQuantity: ez.recyc_onsite_curr_yr_qty, ToxicEquivalencyIdentification: null };
    } else if (section === 'OffsiteRecycledQuantity') {
      return ez.recyc_offsite_curr_yr_na === '1' || ez.recyc_offsite_curr_yr_qty === null
        ? 'NA'
        : { TotalQuantity: ez.recyc_offsite_curr_yr_qty, ToxicEquivalencyIdentification: null };
    } else if (section === 'OnsiteTreatedQuantity') {
      return ez.treated_onsite_curr_yr_na === '1' || ez.treated_onsite_curr_yr_qty === null
        ? 'NA'
        : { TotalQuantity: ez.treated_onsite_curr_yr_qty, ToxicEquivalencyIdentification: null };
    } else if (section === 'OffsiteTreatedQuantity') {
      return ez.treated_offsite_curr_yr_na === '1' || ez.treated_offsite_curr_yr_qty === null
        ? 'NA'
        : {
            TotalQuantity: ez.treated_offsite_curr_yr_qty,
            ToxicEquivalencyIdentification: null,
          };
    } else {
      const unexpected: never = section;
      console.error(`ezToSection8Value: Unexpected section: ${unexpected}`);
      return 'NA';
    }
  }

  if (section8PriorYearQuantitiesFromCalculation) {
    const fromCalc = section8PriorYearQuantitiesFromCalculation[section];
    if (fromCalc === 'NA') {
      return fromCalc;
    } else {
      return {
        TotalQuantity: fromCalc.TotalQuantity,
        ToxicEquivalencyIdentification: null,
      };
    }
  }

  const datum = section8CurrentYearQuantities[section];
  if (datum === 'NA' || ProductionRatio.choice === 'NA') {
    return 'NA';
  } else {
    let ratio: number;
    if (ProductionRatio.choice === 'ProductionRatioMeasure' && typeof ProductionRatio.value === 'number') {
      ratio = ProductionRatio.value;
    } else {
      ratio = 1;
    }

    // Ensure we keep the same number of decimal digits as the current year value:
    const formattedCurrentYearQuantity = formRTotalQuantityToString(datum.TotalQuantity);
    const digitsAfterDecimalPlace = (formattedCurrentYearQuantity.split('.')[1] ?? '').length;
    return {
      TotalQuantity: Number((datum.TotalQuantity / ratio).toFixed(digitsAfterDecimalPlace)),
      ToxicEquivalencyIdentification: null,
    };
  }
}

export function totalAmountsByChemicalComputeFromThresholds(thresholds: ThresholdBrief[]) {
  const totalAmountsByChemical = new Map<CasOrCategory, { pounds: number; grams: number }>();
  for (const threshold of thresholds) {
    if (!totalAmountsByChemical.has(threshold.casOrCategory)) {
      totalAmountsByChemical.set(threshold.casOrCategory, { pounds: 0, grams: 0 });
    }

    let { pounds, grams } = totalAmountsByChemical.get(threshold.casOrCategory) ?? { pounds: 0, grams: 0 };
    pounds += threshold.totalPoundsUsed;
    grams += threshold.totalGramsUsed;
    totalAmountsByChemical.set(threshold.casOrCategory, { pounds, grams });
  }

  return totalAmountsByChemical;
}

export function congenerGramsByCasFromLines(lines: CalculationLine<CasOrCategory>[]): Map<CasOrCategory, number> {
  const grams = UnitsTracker.fromSingleUnit(Gram);
  const result = new Map<CasOrCategory, number>();

  for (const line of lines) {
    const cas = line.casOrCategoryOriginal;
    if (cas === dioxinsCategory || !(cas in dioxinCongenerToFormRField)) {
      continue;
    }

    const converted = UnitsTracker.fromParsedUnitString(line.computationUnits).convertTo(grams, line.computationResult);
    if (typeof converted === 'number') {
      result.set(cas, (result.get(cas) ?? 0) + converted);
    }
  }

  return result;
}

export function transfersToXmlCongeners(
  casOrCategory: CasOrCategory,
  formRLine: FormRLeafLine,
  transfers: TransferComputed[],
  warnings: Warning[],
  multiplier = 1,
): TRIToxicEquivalencyIdentificationType | null {
  if (casOrCategory !== dioxinsCategory) {
    return null;
  }

  const chemicals = new Set<CasOrCategory>();
  const releaseProfilesWithN150 = new Set<string>();
  const releaseProfilesWithCongeners = new Set<string>();
  const congenerLines = new Map<CasOrCategory, CalculationLine[]>();
  for (const transfer of transfers) {
    for (const line of transfer.calculationLines) {
      chemicals.add(line.casOrCategoryOriginal);

      if (line.casOrCategoryOriginal === dioxinsCategory) {
        releaseProfilesWithN150.add(line.releaseProfileName);
      } else {
        releaseProfilesWithCongeners.add(line.releaseProfileName);

        if (!congenerLines.has(line.casOrCategoryOriginal)) {
          congenerLines.set(line.casOrCategoryOriginal, []);
        }
        congenerLines.get(line.casOrCategoryOriginal)!.push(line);
      }
    }
  }

  if (chemicals.size === 0 || chemicals.has(dioxinsCategory)) {
    if (chemicals.size > 1) {
      warnings.push({
        warningID: 'TW1120',
        type: 'internalConsistencyCheck',
        message: `When reporting Dioxins, we must report either the whole category or report the individual congeners. On Form R line ${formRLine} we found computations for: ${Array.from(chemicals).join(', ')}. These release profiles contain the Dioxin category N150: ${Array.from(releaseProfilesWithN150).join(', ')}. These release profiles contain the congeners: ${Array.from(releaseProfilesWithCongeners).join(', ')}.`,
      });
    }

    return null;
  } else {
    const congeners: TRIToxicEquivalencyIdentificationType = {
      ToxicEquivalency1Value: 0,
      ToxicEquivalency2Value: 0,
      ToxicEquivalency3Value: 0,
      ToxicEquivalency4Value: 0,
      ToxicEquivalency5Value: 0,
      ToxicEquivalency6Value: 0,
      ToxicEquivalency7Value: 0,
      ToxicEquivalency8Value: 0,
      ToxicEquivalency9Value: 0,
      ToxicEquivalency10Value: 0,
      ToxicEquivalency11Value: 0,
      ToxicEquivalency12Value: 0,
      ToxicEquivalency13Value: 0,
      ToxicEquivalency14Value: 0,
      ToxicEquivalency15Value: 0,
      ToxicEquivalency16Value: 0,
      ToxicEquivalency17Value: 0,
      ToxicEquivalencyNAIndicator: false,
    };

    const grams = UnitsTracker.fromSingleUnit(Gram);
    for (const [cas, toxicEquivalencyKey] of Object.entries(dioxinCongenerToFormRField)) {
      const lines = congenerLines.get(cas as CasOrCategory) ?? [];
      let congenerGrams = 0;
      for (const line of lines) {
        const converted = UnitsTracker.fromParsedUnitString(line.computationUnits).convertTo(
          grams,
          line.computationResult,
        );
        if (typeof converted === 'number') {
          congenerGrams += converted;
        } else {
          warnings.push({
            warningID: 'TW1121',
            type: 'internalConsistencyCheck',
            message: `Failed to convert ${line.computationUnits} to grams for ${casOrCategory} on Form R line ${formRLine}`,
          });
        }
      }

      congeners[toxicEquivalencyKey] = congenerGrams * multiplier;
    }

    return congeners;
  }
}
